import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StatusHighlight, StatusMatrixEntry } from '../hooks/useOverviewData';
import { formatCurrency, formatNumber } from '../utils/format';
import { downloadCsv, todayYmd } from '../utils/csv';
import ExportButton from './ExportButton';

import offerSvg from '../../../assets/icons/offer.svg';
import agreementSvg from '../../../assets/icons/agreement.svg';
import transportSvg from '../../../assets/icons/transport.svg';
import installationSvg from '../../../assets/icons/installation.svg';
import serviceSvg from '../../../assets/icons/service.svg';

interface Props {
    entries: StatusMatrixEntry[] | null;
    previous?: StatusMatrixEntry[] | null;
    loading: boolean;
}

const ENTITY_ICON: Record<string, string> = {
    offers: offerSvg,
    agreements: agreementSvg,
    transports: transportSvg,
    installations: installationSvg,
    servicing: serviceSvg,
};

const ENTITY_LABEL: Record<string, { id: string; defaultMessage: string }> = {
    offers: { id: 'analyticsV2.entity.offers', defaultMessage: 'Offers' },
    agreements: { id: 'analyticsV2.entity.agreements', defaultMessage: 'Agreements' },
    transports: { id: 'analyticsV2.entity.transports', defaultMessage: 'Transports' },
    installations: { id: 'analyticsV2.entity.installations', defaultMessage: 'Installations' },
    servicing: { id: 'analyticsV2.entity.servicing', defaultMessage: 'Servicing' },
};

const ENTITY_TINT: Record<string, { bg: string; accent: string }> = {
    offers: { bg: 'rgba(96, 165, 250, 0.07)', accent: '#3b82f6' },
    agreements: { bg: 'rgba(167, 139, 250, 0.07)', accent: '#7c3aed' },
    transports: { bg: 'rgba(20, 184, 166, 0.07)', accent: '#0d9488' },
    installations: { bg: 'rgba(52, 211, 153, 0.07)', accent: '#16a34a' },
    servicing: { bg: 'rgba(244, 114, 182, 0.07)', accent: '#db2777' },
};

const DEFAULT_TINT = { bg: 'var(--av2-surface-subtle)', accent: 'var(--av2-accent)' };

const EntityStatusGrid: React.FC<Props> = ({ entries, previous, loading }) => {
    if (loading || !entries) {
        return <EntityStatusGridSkeleton />;
    }
    if (entries.length === 0) {
        return null;
    }

    const prevTotals = new Map<string, number>();
    (previous ?? []).forEach((e) => prevTotals.set(e.entity, e.total));

    const handleExport = () => {
        type Row = { entity: string; entityTotal: number; status: string; count: number };
        const rows: Row[] = [];
        for (const e of entries) {
            for (const s of e.statuses) {
                rows.push({ entity: e.label, entityTotal: e.total, status: s.label, count: s.count });
            }
        }
        downloadCsv<Row>(
            `entity-status-${todayYmd()}.csv`,
            [
                { header: 'Entity', accessor: (r) => r.entity },
                { header: 'Entity total', accessor: (r) => r.entityTotal },
                { header: 'Status', accessor: (r) => r.status },
                { header: 'Count', accessor: (r) => r.count },
            ],
            rows,
        );
    };

    return (
        <section className="av2-card av2-card--flush">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage id="analyticsV2.entityGrid.title" defaultMessage="Where everything stands" />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.entityGrid.subtitle"
                            defaultMessage="Status across every module in the selected period"
                        />
                    </p>
                </div>
                <ExportButton onClick={handleExport} disabled={entries.length === 0} />
            </div>
            <div className="av2-entity-grid">
                {entries.map((entry) => (
                    <EntityStatusCard key={entry.entity} entry={entry} prevTotal={prevTotals.get(entry.entity) ?? null} />
                ))}
            </div>
        </section>
    );
};

const EntityStatusCard: React.FC<{ entry: StatusMatrixEntry; prevTotal: number | null }> = ({ entry, prevTotal }) => {
    const intl = useIntl();
    const icon = ENTITY_ICON[entry.entity];
    const labelMeta = ENTITY_LABEL[entry.entity] || { id: `analyticsV2.entity.${entry.entity}`, defaultMessage: entry.label };
    const tint = ENTITY_TINT[entry.entity] || DEFAULT_TINT;
    const highlight = deriveHighlight(entry);
    // Neutral direction badge (arrow only) — "more activity" isn't unambiguously
    // good across every entity (e.g. servicing).
    const delta =
        prevTotal != null && prevTotal > 0
            ? Math.round(((entry.total - prevTotal) / prevTotal) * 1000) / 10
            : null;

    return (
        <article className="av2-entity-card" style={{ background: tint.bg, ['--entity-accent' as any]: tint.accent }}>
            <div className="av2-entity-card__head">
                {icon && <img className="av2-entity-card__icon" src={icon} alt="" aria-hidden="true" />}
                <span className="av2-entity-card__name">
                    <FormattedMessage id={labelMeta.id} defaultMessage={labelMeta.defaultMessage} />
                </span>
                {delta != null && Math.abs(delta) >= 0.1 && (
                    <span className="av2-entity-card__delta" title={`${formatNumber(prevTotal!, intl.locale)} → ${formatNumber(entry.total, intl.locale)}`}>
                        {delta > 0 ? '↑' : '↓'} {delta > 0 ? '+' : ''}
                        {formatRate(delta, intl.locale)}%
                    </span>
                )}
            </div>

            <div className="av2-entity-card__donut-row">
                <Donut segments={entry.statuses} total={entry.total} locale={intl.locale} />
                <ul className="av2-entity-card__legend">
                    {entry.statuses.map((s) => (
                        <li key={s.key} className="av2-entity-card__legend-row">
                            <span className="av2-entity-card__legend-dot" style={{ background: s.color }} aria-hidden="true" />
                            <span className="av2-entity-card__legend-name">{s.label}</span>
                            <span className="av2-entity-card__legend-count">{formatNumber(s.count, intl.locale)}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {highlight && (
                <div className="av2-entity-card__metric-foot">
                    <span className="av2-entity-card__metric-foot-label">
                        <FormattedMessage
                            id={`analyticsV2.entityGrid.metric.${highlight.key}`}
                            defaultMessage={METRIC_DEFAULTS[highlight.key] ?? ''}
                        />
                    </span>
                    <span className="av2-entity-card__metric-foot-value">{formatHighlight(highlight, intl.locale)}</span>
                </div>
            )}
        </article>
    );
};

const DONUT_R = 30;
const DONUT_C = 2 * Math.PI * DONUT_R;

const Donut: React.FC<{ segments: StatusMatrixEntry['statuses']; total: number; locale: string }> = ({ segments, total, locale }) => {
    let acc = 0;
    return (
        <svg width="76" height="76" viewBox="0 0 76 76" role="img" aria-label="status breakdown" className="av2-entity-card__donut">
            <circle cx="38" cy="38" r={DONUT_R} fill="none" stroke="var(--av2-border)" strokeWidth="9" />
            {total > 0 &&
                segments.map((s) => {
                    const frac = s.count / total;
                    if (frac <= 0) return null;
                    const dash = frac * DONUT_C;
                    const rot = -90 + acc * 360;
                    acc += frac;
                    return (
                        <circle
                            key={s.key}
                            cx="38"
                            cy="38"
                            r={DONUT_R}
                            fill="none"
                            stroke={s.color}
                            strokeWidth="9"
                            strokeDasharray={`${dash.toFixed(2)} ${(DONUT_C - dash).toFixed(2)}`}
                            transform={`rotate(${rot.toFixed(2)} 38 38)`}
                        />
                    );
                })}
            <text x="38" y="42" textAnchor="middle" fontSize="15" fontWeight="500" fill="var(--av2-text-primary)">
                {formatNumber(total, locale)}
            </text>
        </svg>
    );
};

const METRIC_DEFAULTS: Record<string, string> = {
    totalValue: 'Total value',
    invoiced: 'Invoiced',
    delivered: 'Delivered',
    completed: 'Completed',
    open: 'Open',
};

function deriveHighlight(entry: StatusMatrixEntry): StatusHighlight | null {
    if (entry.highlight) return entry.highlight;
    const total = entry.total || 0;
    const findKey = (k: string) => entry.statuses.find((s) => s.key === k)?.count ?? 0;
    const sumKeys = (keys: string[]) => entry.statuses.filter((s) => keys.includes(s.key)).reduce((a, s) => a + s.count, 0);
    switch (entry.entity) {
        case 'transports':
            return { format: 'percent', key: 'delivered', value: total > 0 ? (findKey('transport_2') / total) * 100 : 0 };
        case 'installations':
            return { format: 'percent', key: 'completed', value: total > 0 ? (findKey('install_4') / total) * 100 : 0 };
        case 'servicing':
            return { format: 'number', key: 'open', value: sumKeys(['srv_1', 'srv_2', 'srv_3', 'srv_4']) };
        default:
            return null;
    }
}

function formatHighlight(h: StatusHighlight, locale: string): string {
    if (h.format === 'money') return formatCurrency(h.value, locale);
    if (h.format === 'percent') return `${Math.round(h.value)}%`;
    return formatNumber(h.value, locale);
}

const formatRate = (n: number, locale: string): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(n);

const EntityStatusGridSkeleton: React.FC = () => {
    return (
        <section className="av2-card av2-card--flush">
            <div className="av2-card__header">
                <div className="av2-skel av2-skel--card-title" />
            </div>
            <div className="av2-entity-grid">
                {[0, 1, 2, 3, 4].map((i) => (
                    <article key={i} className="av2-entity-card av2-entity-card--skeleton">
                        <div className="av2-skel av2-skel--label" />
                        <div className="av2-entity-card__donut-row">
                            <div className="av2-skel" style={{ width: 64, height: 64, borderRadius: '50%' }} />
                            <div style={{ flex: 1 }}>
                                <div className="av2-skel av2-skel--label" style={{ marginBottom: 8 }} />
                                <div className="av2-skel av2-skel--label" />
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
};

export default EntityStatusGrid;
